import React, { useCallback, useRef } from "react";
import { List } from "../../Components/common/List/List";
import { ListProvider } from "../../Components/common/List/Providers/List";
import { useListProps } from "./utilities/list.js";
import { Feedback } from "../../Models/Feedback.ts";
import { Modal, ModalBody, UncontrolledTooltip } from "reactstrap";
import { useIndexContext } from "./providers/Index.tsx";
import { createModalConfig, ModalConfig } from "../../Models/ModalConfig.ts";
import { useAppContext } from "../../AppProvider.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBagsShopping, faTruck } from "../../utilities/fontawesomeIcons.js";
import { Handler as HandlerFeedback } from "../Feedback/Handler";
import { HandlerProvider as HandlerFeedbackProvider } from "../Feedback/providers/Handler";
import Rating from "../../Components/Rating/Rating.tsx";
import { Link } from "react-router-dom";
import moment from "moment";

const AnyListProvider = ListProvider as any;
const AnyList = List as any;

export const Index: React.FC = () => {
  const listRef = useRef<{ refresh: () => void } | null>(null);
  const listProps = useListProps();
  const indexContext = useIndexContext();
  const modalConfig = indexContext?.modalConfig;
  const setModalConfig = indexContext?.setModalConfig;

  const renderItem = useCallback((item: Feedback, index: number) => {
    return <ListItem listRef={listRef} item={item} index={index} />;
  }, []);

  const closeModal = () => {
    if (setModalConfig) {
      setModalConfig(createModalConfig());
    }
  };

  return (
    <>
      <AnyListProvider
        path={listProps.path}
        filters={listProps.filters}
        sorting={listProps.sorting}
        defaultDate={listProps.defaultDate}
      >
        <AnyList
          banner={listProps.banner}
          renderItem={renderItem}
          pagination={listProps.pagination}
          omnisearch={listProps.omnisearch}
          selectRowsEnabled={listProps.selectRowsEnabled}
          type={listProps.type}
        />
      </AnyListProvider>
      <Modal isOpen={modalConfig?.isOpen} size={modalConfig?.size}>
        <ModalBody className={modalConfig?.className || ""}>
          {modalConfig?.action === "feedback" && (
            <HandlerFeedbackProvider
              id={modalConfig?.data?.[0]}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
            >
              <HandlerFeedback />
            </HandlerFeedbackProvider>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default Index;

const ListItem: React.FC<{
  listRef: React.RefObject<{
    refresh: () => void;
  } | null>;
  item: Feedback;
  index: number;
}> = React.memo(({ item, index }) => {
  const { dictionary } = useAppContext();
  const indexContext = useIndexContext();
  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = ""
    ) => {
      indexContext?.setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [indexContext]
  );

  const gridTemplateColumns = "1fr 1fr";

  const gridTemplateAreas = `
      "logo logo"
      "rating rating"
      "date date"
      "reference orderReference"
      "row row"
      "detailOrder detailShipment"
    `;

  return (
    <div
      className="p-3 cursor-pointer "
      onClick={() => {
        openModal(
          "feedback",
          [item.id],
          "md",
          "",
          dictionary.feedbacks.feedback
        );
      }}
    >
      <div
        className="d-grid"
        style={{
          gridTemplateColumns: gridTemplateColumns,
          gridTemplateAreas: gridTemplateAreas,
          gap: "0.5rem 1rem",
        }}
      >
        <div
          className="d-flex justify-content-center "
          style={{ gridArea: "logo" }}
        >
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              backgroundColor: "#f5f5f5",
              gap: "2rem",
            }}
          >
            {item.owner && (
              <>
                <img
                  id={"owner_" + index}
                  src={item.owner.logoDarkUrl}
                  style={{
                    width: "auto",
                    height: "60%",
                    objectFit: "contain",
                  }}
                />
                <UncontrolledTooltip
                  placement="right"
                  target={"owner_" + index}
                >
                  <label className="label-2 text-light">
                    {dictionary.registries.company}:
                  </label>
                  <label className="d-block">{item.owner.name || "-"}</label>
                  {item.owner.externalReference && (
                    <label className="fs-xs">
                      ({item.owner.externalReference})
                    </label>
                  )}
                </UncontrolledTooltip>
              </>
            )}
          </div>
        </div>
        <div
          style={{ gridArea: "rating" }}
          className="d-flex justify-content-center"
        >
          <Rating value={item.rating} hidden={true} />
        </div>

        <div style={{ gridArea: "date" }} className="text-center">
          <label className="label-2 cursor-pointer">
            {dictionary.dates.date}
          </label>

          <label className="label-3 cursor-pointer">
            {moment(item.date).format("DD/MM/YYYY HH:mm")}
          </label>
        </div>

        <hr
          className="my-2 "
          style={{ gridArea: "row", border: "1px solid #ccc" }}
        />

        {item.orderReference && (
          <Link
            style={{
              gridArea: "detailOrder",
            }}
            title={`${dictionary.actions.details} ${dictionary.orders.order}`}
            className="button-1 button-1-light"
            to={`/orders/details/${item.orderReference}`}
          >
            <FontAwesomeIcon icon={faBagsShopping} />
            <span>{dictionary.orders.order}</span>
          </Link>
        )}
        {item.reference && (
          <Link
            style={{
              gridArea: "detailShipment",
            }}
            title={`${dictionary.actions.details} ${dictionary.shipments.shipment}`}
            className="button-1 button-1-light"
            to={`/shipments/details/${item.reference}`}
          >
            <FontAwesomeIcon
              icon={faTruck}
              className={`${!item.reference && "text-white"}`}
            />
            <span>{dictionary.shipments.shipment}</span>
          </Link>
        )}
      </div>
    </div>
  );
});
